import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useState, useEffect } from "react";
import {
  LayoutDashboard, BarChart3, LogOut, Bell, Shield, Briefcase,
  AlertTriangle, Wallet, ArrowLeftRight, GitCompare, Building,
  ChevronLeft, ChevronRight, ChevronDown,
  Inbox, ClipboardCheck,
} from "lucide-react";
import TopbarSearch from "./TopbarSearch";

const BD_NAV = [
  { to: "/dashboard",    label: "Dashboard",          icon: LayoutDashboard, color: "#FFCC00", testid: "nav-dashboard" },
  {
    label: "Property IC",
    icon: Briefcase,
    color: "#FF69B4",
    testid: "nav-property-ic",
    // Presence of `children` turns this into a collapsible dropdown group.
    // Any child route auto-expands the group; each child has its own active state.
    matchPrefix: "/property-ic",
    children: [
      { to: "/property-ic/drafts",         label: "BD Drafts & Mails", icon: Inbox,          testid: "nav-property-ic-drafts" },
      { to: "/property-ic/submission/new", label: "IC Submission",     icon: ClipboardCheck, testid: "nav-property-ic-submission", matchPrefix: "/property-ic/submission" },
    ],
  },
  { to: "/compare",      label: "Property wise data", icon: BarChart3,       color: "#20B2AA", testid: "nav-compare" },
];

const MIS_NAV = [
  { to: "/mis/dashboard",       label: "MIS Dashboard",     icon: LayoutDashboard, color: "#20B2AA", testid: "nav-mis-dashboard" },
  { to: "/mis/expenses",        label: "Expense Tracker",   icon: Wallet,          color: "#FB7185", testid: "nav-mis-expenses" },
  { to: "/mis/property-tracker", label: "Property Tracker", icon: Building,        color: "#FF69B4", testid: "nav-mis-property-tracker" },
  { to: "/mis/outliers",        label: "Outlier Watch",     icon: AlertTriangle,   color: "#FFA500", testid: "nav-mis-outliers" },
  { to: "/mis/compare",         label: "Property Compare",  icon: GitCompare,      color: "#9370DB", testid: "nav-mis-compare" },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Admin", icon: Shield, color: "#9370DB", testid: "nav-admin" },
];

const PERSONA_THEME = {
  bd:  { sidebarSub: "Business development portal", accent: "#FF69B4", brandLine: "" },
  mis: { sidebarSub: "MIS analysis · cost intelligence", accent: "#20B2AA", brandLine: "" },
};

export default function AppShell({ children, title, subtitle, persona: personaProp }) {  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Sidebar collapse — persisted per-user in localStorage. Purely visual.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("sidebar_collapsed", collapsed ? "1" : "0"); } catch { /* private mode */ }
  }, [collapsed]);
  // Auto-detect persona from route if not explicitly passed
  const persona = personaProp || (location.pathname.startsWith("/mis/") ? "mis" : "bd");
  const theme = PERSONA_THEME[persona] || PERSONA_THEME.bd;
  const nav = persona === "mis" ? MIS_NAV : BD_NAV;
  const navItems = [...nav, ...(user?.is_admin ? ADMIN_NAV : [])];
  const homeRoute = persona === "mis" ? "/mis/dashboard" : "/dashboard";

  return (
    <div className="min-h-screen flex bg-brand-paper" data-testid={`app-shell-${persona}`}>
      {/* Sidebar */}
      <aside
        className={`${collapsed ? "w-16" : "w-64"} bg-brand-ink text-zinc-200 flex flex-col flex-shrink-0 border-r border-zinc-800 transition-[width] duration-200 ease-out sticky top-0 h-screen z-20`}
        data-testid="sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        {/* Collapse toggle — sits on the outer edge of the sidebar */}
        <button
          onClick={() => setCollapsed(v => !v)}
          data-testid="sidebar-toggle"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="absolute -right-3 top-8 z-20 w-6 h-6 rounded-full bg-brand-ink border border-zinc-700 hover:border-brand-yellow flex items-center justify-center transition-colors"
        >
          {collapsed ? <ChevronRight className="w-3 h-3 text-zinc-300" /> : <ChevronLeft className="w-3 h-3 text-zinc-300" />}
        </button>

        <div
          className={`${collapsed ? "px-3 justify-center" : "px-5"} py-6 border-b border-zinc-800 flex items-center gap-3 cursor-pointer`}
          onClick={() => navigate(homeRoute)}
          data-testid="sidebar-logo"
        >
          <img src="/logo.webp" alt="goSTOPS" className={`${collapsed ? "w-9 h-9" : "w-11 h-11"} rounded-xl object-cover flex-shrink-0`} />
          {!collapsed && (
            <div>
              <div className="font-display font-black text-base leading-none tracking-tight text-white logo-wordmark">
                <span>g</span><span>o</span><span>S</span><span>T</span><span>O</span><span>P</span><span>S</span>
              </div>
              <div className="font-mono-tech text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1.5">{theme.sidebarSub}</div>
            </div>
          )}
        </div>

        {/* Persona switcher pill */}
        <button
          onClick={() => navigate("/persona")}
          data-testid="persona-switch"
          title={collapsed ? `Switch workspace (${persona === "mis" ? "MIS" : "BD"})` : undefined}
          className={`${collapsed ? "mx-2 justify-center" : "mx-3"} mt-3 px-3 py-2 rounded-lg border border-zinc-800 hover:border-zinc-600 text-[11px] text-zinc-400 hover:text-white flex items-center gap-2 transition-colors`}
        >
          <ArrowLeftRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: theme.accent }} />
          {!collapsed && (
            <>
              <span>Switch workspace</span>
              <span className="ml-auto text-[9.5px] uppercase tracking-wider font-bold" style={{ color: theme.accent }}>
                {persona === "mis" ? "MIS" : "BD"}
              </span>
            </>
          )}
        </button>

        <nav className={`flex-1 ${collapsed ? "px-2" : "px-3"} py-4 space-y-1`}>
          {navItems.map((item) => {
            // Grouped item (dropdown with children) — currently only "Property IC"
            if (item.children) {
              return (
                <NavGroup
                  key={item.label}
                  item={item}
                  collapsed={collapsed}
                  currentPath={location.pathname}
                />
              );
            }
            const { to, label, icon: Icon, color, testid } = item;
            return (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                title={collapsed ? label : undefined}
                className={({ isActive }) =>
                  `relative flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2.5 text-sm font-medium transition-all rounded-lg group ${
                    isActive
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-400 hover:bg-zinc-900/60 hover:text-white"
                  }`
                }
                style={({ isActive }) => isActive ? { boxShadow: `inset 3px 0 0 0 ${color}` } : {}}
              >
                {({ isActive }) => (
                  <>
                    <Icon className="w-4 h-4 flex-shrink-0 transition-colors" strokeWidth={2} style={{ color: isActive ? color : undefined }} />
                    {!collapsed && <span className="font-display font-bold tracking-tight">{label}</span>}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className={`${collapsed ? "px-2 py-2" : "px-3 py-2"} border-t border-zinc-800`} data-testid="sidebar-profile">
          <div
            className={`relative flex items-center ${collapsed ? "flex-col gap-2 px-2" : "gap-3 px-3"} py-2.5 text-sm font-medium rounded-lg text-zinc-400 hover:bg-zinc-900/60 hover:text-white transition-all`}
          >
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover ring-2 flex-shrink-0" style={{ ringColor: theme.accent }} data-testid="user-avatar" title={collapsed ? `${user?.name} · ${user?.email}` : undefined} />
            ) : (
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black text-brand-ink flex-shrink-0" style={{ background: theme.accent }} title={collapsed ? `${user?.name} · ${user?.email}` : undefined}>
                {(user?.name || "U").slice(0, 1)}
              </div>
            )}
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold tracking-tight text-white truncate text-[13px]" data-testid="user-name">{user?.name}</div>
                <div className="text-[10px] text-zinc-500 truncate">{user?.email}</div>
              </div>
            )}
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="text-zinc-400 hover:text-brand-yellow p-1.5 rounded-md transition-colors flex-shrink-0"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-brand-line flex items-center justify-between px-8 h-16" data-testid="topbar">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">{subtitle || ""}</div>
            <h1 className="font-display font-black text-xl text-brand-ink leading-none tracking-tight mt-1" data-testid="page-title">
              {title}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <TopbarSearch />
            <button className="p-2 rounded-full border border-brand-line bg-white hover:bg-brand-yellow hover:border-brand-ink transition-colors" data-testid="notifications-btn">
              <Bell className="w-4 h-4 text-zinc-700" />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-8" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}


// Collapsible nav group — used for Property IC's Drafts + Submission children.
// Auto-expands whenever the current path matches the group's route prefix so
// child items are visible right when you arrive on any child page.
function NavGroup({ item, collapsed, currentPath }) {
  const groupActive = currentPath.startsWith(item.matchPrefix);
  const [open, setOpen] = useState(groupActive);
  useEffect(() => { if (groupActive) setOpen(true); }, [groupActive]);
  const Icon = item.icon;

  // Collapsed rail: parent icon always visible; child icons auto-expand ONLY
  // when the user is currently on a Property IC child route. Clicking the
  // parent lands them on the first child, which switches groupActive → true
  // and reveals both children below on the next render.
  if (collapsed) {
    return (
      <div className="space-y-0.5" data-testid={`${item.testid}-collapsed-group`}>
        <NavLink
          to={item.children[0].to}
          data-testid={item.testid}
          title={item.label}
          className={`relative flex items-center justify-center px-2 py-2.5 text-sm font-medium transition-all rounded-lg group ${
            groupActive ? "bg-zinc-900 text-white" : "text-zinc-400 hover:bg-zinc-900/60 hover:text-white"
          }`}
          style={groupActive ? { boxShadow: `inset 3px 0 0 0 ${item.color}` } : {}}
        >
          <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} style={{ color: groupActive ? item.color : undefined }} />
        </NavLink>
        {groupActive && item.children.map((c) => {
          const ChildIcon = c.icon;
          const isChildActive = c.matchPrefix ? currentPath.startsWith(c.matchPrefix) : currentPath === c.to;
          return (
            <NavLink
              key={c.to}
              to={c.to}
              data-testid={c.testid}
              title={c.label}
              className={`relative flex items-center justify-center mx-1.5 px-2 py-2 text-sm font-medium transition-all rounded-lg ${
                isChildActive ? "bg-zinc-900 text-white" : "text-zinc-400 hover:bg-zinc-900/60 hover:text-white"
              }`}
              style={isChildActive ? { boxShadow: `inset 3px 0 0 0 ${item.color}` } : {}}
            >
              <ChildIcon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2} style={{ color: isChildActive ? item.color : undefined }} />
            </NavLink>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid={item.testid}
        className={`w-full relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all rounded-lg ${
          groupActive ? "bg-zinc-900 text-white" : "text-zinc-400 hover:bg-zinc-900/60 hover:text-white"
        }`}
        style={groupActive ? { boxShadow: `inset 3px 0 0 0 ${item.color}` } : {}}
      >
        <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} style={{ color: groupActive ? item.color : undefined }} />
        <span className="font-display font-bold tracking-tight">{item.label}</span>
        <ChevronDown className={`ml-auto w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-4 border-l border-zinc-800 pl-2 space-y-0.5" data-testid={`${item.testid}-children`}>
          {item.children.map((c) => {
            const isChildActive = c.matchPrefix ? currentPath.startsWith(c.matchPrefix) : currentPath === c.to;
            return (
              <NavLink
                key={c.to}
                to={c.to}
                data-testid={c.testid}
                className={`block px-3 py-2 text-[13px] rounded-lg transition-all ${
                  isChildActive
                    ? "bg-zinc-900 text-white font-display font-bold tracking-tight"
                    : "text-zinc-400 hover:bg-zinc-900/60 hover:text-white font-medium"
                }`}
                style={isChildActive ? { boxShadow: `inset 3px 0 0 0 ${item.color}` } : {}}
              >
                {c.label}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}
